import React from 'react';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { getSentryConfig, isSentryEnabled, logSentryConfig } from '../config/sentry.resolver';

let isInitialized = false;

/**
 * Initialize Sentry error monitoring.
 * Must be called before any other initialization (including GA4).
 */
export const initSentry = (): void => {
  if (isInitialized) return;

  if (!isSentryEnabled()) {
    console.log('[Sentry] Disabled for this environment');
    return;
  }

  const config = getSentryConfig();

  if (!config.dsn) {
    console.warn('[Sentry] No DSN configured. Check sentry.resolver.ts or set VITE_SENTRY_DSN');
    return;
  }

  if (config.debug) {
    logSentryConfig();
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: import.meta.env.VITE_APP_VERSION || 'unknown',
      debug: config.debug,
      sendDefaultPii: true,
      enableLogs: true,

      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration(),
        Sentry.breadcrumbsIntegration(),
      ],

      tracesSampleRate: config.tracesSampleRate,
      tracePropagationTargets: ['localhost', /^https:\/\/.*\.skill-mine\.com/],
      profileSessionSampleRate: 1.0,
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

      beforeSend(event, hint) {
        const error = hint?.originalException;

        // Filter out 401 / token refresh errors
        if (error instanceof Error) {
          if (error.message?.includes('401') || error.message?.includes('Token refresh failed')) {
            return null;
          }
        }

        // Filter out Axios errors we don't want
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401) return null;
          const errorData = error.response?.data as any;
          const errorCode = errorData?.error?.error || errorData?.error;
          if (errorCode === 'tenant_inactive' || errorCode === 'tenant_suspended') return null;
        }

        // Filter out offline/network errors
        if (!navigator.onLine) return null;

        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data) {
          const sensitiveKeys = ['authorization', 'bearer', 'password', 'token'];
          const data = { ...breadcrumb.data };

          for (const key of Object.keys(data)) {
            if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
              data[key] = '[REDACTED]';
            }
          }

          if (typeof data.body === 'string') {
            data.body = data.body.replace(
              /("(?:password|token|authorization|bearer)":\s*")[^"]*"/gi,
              '$1[REDACTED]"'
            );
          }

          breadcrumb.data = data;
        }
        return breadcrumb;
      },

      ignoreErrors: [
        'ResizeObserver loop',
        'Non-Error promise rejection',
        'ChunkLoadError',
        'Network Error',
        'Load failed',
        'Failed to fetch',
      ],

      denyUrls: [
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
      ],
    });

    isInitialized = true;
    console.log(`[Sentry] Initialized (env: ${config.environment}, debug: ${config.debug})`);
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
};

export const isSentryInitialized = (): boolean => isInitialized;

export const setSentryUser = (user: { id: string; email?: string; name?: string } | null): void => {
  if (!isInitialized) return;

  if (user) {
    Sentry.setUser({ id: user.id, email: user.email, username: user.name });
  } else {
    Sentry.setUser(null);
  }
};

export const setSentryTenant = (tenantKey: string | null): void => {
  if (!isInitialized) return;

  Sentry.setTag('tenant', tenantKey || undefined);
};

export const captureApiError = (
  error: AxiosError,
  context?: Record<string, unknown>
): void => {
  if (!isInitialized) return;

  Sentry.withScope((scope) => {
    scope.setTag('api_error', true);

    const method = (error.config?.method || 'GET').toUpperCase();
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 0;

    scope.setFingerprint([method, url, String(status)]);
    scope.setExtras({
      method,
      url,
      status,
      response: truncate(JSON.stringify(error.response?.data), 1000),
      ...context,
    });

    Sentry.captureException(error);
  });
};

export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void => {
  if (!isInitialized) return;

  Sentry.addBreadcrumb({ category, message, data, level });
};

// Helpers

function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

const sentry = {
  init: initSentry,
  isInitialized: isSentryInitialized,
  setUser: setSentryUser,
  setTenant: setSentryTenant,
  captureApiError,
  addBreadcrumb,
};

export default sentry;
